import {
  useEffect, useState, useMemo, useRef,
} from 'react';
import { useSelector } from 'react-redux';

import { makeStyles } from '@material-ui/core/styles';
import {
  Box, CircularProgress, TextField, Tooltip,
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import { COLORS } from 'src/constants/Color';
import { Store, useAppDispatch } from 'src/redux/Store';
import { setSelections } from 'src/redux/actions/selectionActions';
import { openModal } from 'src/redux/actions/modalActions';
import { openDialog } from 'src/redux/actions/dialogActions';
import { MODALS } from 'src/models/ModalModel';
import { DIALOGS } from 'src/models/DialogModel';
import { ENTITIES } from 'src/models/QuerybuilderModel';
import { postEmailEntitiesData, postDownloadEntitesData } from 'src/apis/QuerybuilderAPI';
import { DELIVERY_TYPE, FORMAT, ORIENTATION } from 'src/models/DownloadModel';
import { DownloadOption } from 'src/lib/makeApiRequest';
import { SEARCH_OPERATORS_PRIMITIVE } from 'src/constants/SearchOperators';
import { setAlert } from 'src/redux/actions/alertActions';
import { ALERT_TYPE } from 'src/constants/AlertType';
import { getPresetsAction } from 'src/redux/actions/presetActions';
import { getCurrenciesAction } from 'src/redux/actions/dataActions';
import { getPaymentData } from 'src/apis/PaymentAPI';
import { PAYMENT_STATUS } from 'src/models/PaymentModel';
import { INVOICE_PAYMENT_STATUS, INVOICE_STATE } from 'src/constants/Invoices';
import { t } from 'src/lib/language';
import { Path } from 'src/constants/routes';
import ReceiptIcon from 'src/components/UI/ReceiptIcon';
import XCircleIcon from 'src/components/UI/XCircleIcon';
import AlertTriangleIcon from 'src/components/UI/AlertTriangleIcon';
import CalendarClockIcon from 'src/components/UI/CalendarClockIcon';
import LinkIcon from 'src/components/UI/LinkIcon';
import GetAppIcon from '@material-ui/icons/GetApp';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
import ReplayIcon from '@material-ui/icons/Replay';
import SendIcon from '@material-ui/icons/Send';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

import SummaryCard from 'src/components/UI/SummaryCard';
import TimePeriodTabs from 'src/components/UI/TimePeriodTabs';
import Row from 'src/components/UI/Row';

import {
  fetchCollectedToday,
  fetchDeclinedPaymentsData,
  fetchUnpaidDonationsData,
  fetchScheduledPayments,
  fetchScheduledPaymentsList,
  buildDateFilter,
  fetchEventsData,
  DashboardCardData,
  DownloadPeriod,
  DeclinedPaymentItem,
  UnpaidDonationItem,
  ScheduledPaymentItem,
  EventItem,
  RecentPaymentItem,
  fetchRecentPaymentsList,
  TopDonorItem,
  fetchTopDonors,
} from 'src/redux/actions/dashboardActions';

const useStyles = makeStyles((theme) => ({
  wrapper: {
    marginTop: 32,
    '@media (max-width: 680px)': {
      marginTop: 80,
    },
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: 16,
    '@media (max-width: 680px)': {
      gap: 8,
    },
  },
  rowGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 20,
    marginTop: 10,
    alignItems: 'flex-start',
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: '1fr',
    },
    '@media (max-width: 680px)': {
      gap: 12,
      marginTop: 6,
    },
  },
  bottomRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 20,
    marginTop: 10,
    alignItems: 'flex-start',
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: '1fr',
    },
    '@media (max-width: 680px)': {
      gap: 12,
      marginTop: 6,
    },
  },
  section: {
    position: 'relative',
    backgroundColor: COLORS.white,
    borderRadius: 12,
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    border: `1px solid ${COLORS.background}`,
    padding: 24,
    marginTop: 10,
    '@media (max-width: 680px)': {
      padding: 12,
      marginTop: 6,
    },
  },
  sectionOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255,255,255,0.7)',
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap' as const,
    gap: 12,
    cursor: 'pointer',
    '@media (max-width: 680px)': {
      gap: 8,
    },
  },
  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 14,
    fontWeight: 600,
    color: '#1E293B',
    minWidth: 0,
  },
  chevron: {
    transition: 'transform 0.2s',
  },
  chevronExpanded: {
    transform: 'rotate(180deg)',
  },
  badge: {
    fontSize: 12,
    fontWeight: 600,
    padding: '2px 10px',
    borderRadius: 999,
  },
  totalBadge: {
    fontSize: 12,
    fontWeight: 600,
    padding: '4px 10px',
    borderRadius: 999,
    backgroundColor: COLORS.redLight,
    color: COLORS.red,
  },
  warningBadge: {
    fontSize: 12,
    fontWeight: 600,
    padding: '4px 10px',
    borderRadius: 999,
    backgroundColor: COLORS.amberLight,
    color: COLORS.amber,
  },
  scheduledBadge: {
    fontSize: 12,
    fontWeight: 600,
    padding: '4px 10px',
    borderRadius: 999,
    backgroundColor: COLORS.primaryLight,
    color: COLORS.primary,
  },
  actionRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flex: 1,
  },
  actionButtons: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginLeft: 'auto',
  },
  downloadButton: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 12,
    fontWeight: 600,
    padding: '6px 12px',
    borderRadius: 8,
    border: `1px solid ${COLORS.download}`,
    backgroundColor: COLORS.download,
    color: COLORS.white,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: COLORS.downloadLight,
      color: COLORS.download,
    },
  },
  primaryButton: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 12,
    fontWeight: 600,
    padding: '6px 12px',
    borderRadius: 8,
    border: `1px solid ${COLORS.primary}`,
    backgroundColor: COLORS.primary,
    color: COLORS.white,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: COLORS.primaryLight,
      color: COLORS.primary,
    },
  },
  ghostButton: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 12,
    fontWeight: 600,
    padding: '6px 12px',
    borderRadius: 8,
    border: `1px solid ${COLORS.secondary}`,
    backgroundColor: COLORS.secondary,
    color: COLORS.white,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: COLORS.secondaryLight,
      color: COLORS.secondary,
    },
  },
  filterRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 12,
    margin: '8px 0',
    '@media (max-width: 680px)': {
      flexWrap: 'wrap' as const,
    },
  },
  filterInput: {
    width: '50%',
    marginLeft: 'auto',
    flexShrink: 0,
    '& .MuiInputBase-root': {
      fontSize: 13,
      backgroundColor: '#F8FAFC',
      borderRadius: 8,
      padding: '0 8px',
      height: 32,
      '&:hover': {
        backgroundColor: '#F1F5F9',
      },
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'transparent',
    },
    '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: COLORS.primary,
    },
    '@media (max-width: 680px)': {
      width: '100%',
      marginLeft: 0,
    },
  },
  listWrapper: {
    maxHeight: 5 * 48,
    overflow: 'auto',
    marginTop: 16,
    '@media (max-width: 680px)': {
      marginTop: 8,
    },
  },
  listItem: {
    borderBottom: `1px solid ${COLORS.background}`,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#F8FAFC',
    },
  },
  donorName: {
    fontWeight: 600,
    fontSize: 14,
    color: '#1E293B',
  },
  donorDetail: {
    fontSize: 12,
    color: '#64748B',
  },
  amount: {
    fontSize: 14,
    fontWeight: 600,
    color: '#1E293B',
  },
  progressBar: {
    height: 4,
    borderRadius: 2,
    backgroundColor: COLORS.background,
    marginTop: 4,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: 2,
    backgroundColor: COLORS.secondary,
  },
}));

const DashboardPage = () => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const organisation = useSelector((state: Store) => state.currentOrganisation.id);
  const organisationCurrencyId = useSelector(
    (state: Store) => state.currentOrganisation.currencyId,
  );
  const currencies = useSelector((state: Store) => state.data.currencies);
  const lang = useSelector((state: Store) => state.language.language ?? 'en');

  const handleEditPayment = (paymentId: number) => {
    dispatch(setSelections({
      entity: ENTITIES.payments,
      selections: [paymentId],
    }));
    dispatch(openModal({ modal: MODALS.editPayment }));
  };

  const handleEditInvoice = (invoiceId: number) => {
    dispatch(setSelections({
      entity: ENTITIES.invoices,
      selections: [invoiceId],
    }));
    dispatch(openModal({ modal: MODALS.editInvoice }));
  };

  const handleSendInvoice = async (invoiceId: number) => {
    const invoiceOptions = {
      file: `invoice_${invoiceId}.pdf`,
      orientation: ORIENTATION.portrait,
      format: FORMAT.pdf,
      delivery: DELIVERY_TYPE.contact,
      export_to_grid: false,
    };
    const response = await postEmailEntitiesData(organisation, ENTITIES.invoices, {
      filters: {
        and: [
          ['invoices.id', SEARCH_OPERATORS_PRIMITIVE.in, [invoiceId]],
        ],
      },
      options: invoiceOptions,
    }, invoiceOptions);
    if (response.success) {
      dispatch(setAlert({ type: ALERT_TYPE.success, code: 'messages.invoice_sent' }));
    } else {
      dispatch(setAlert({ type: ALERT_TYPE.error, code: 'messages.invoice_sent_error' }));
    }
  };

  const handleDownloadInvoice = async (invoiceId: number) => {
    const invoiceOptions = {
      file: `invoice_${invoiceId}.pdf`,
      orientation: ORIENTATION.portrait,
      format: FORMAT.pdf,
      delivery: DELIVERY_TYPE.download,
      export_to_grid: false,
    };
    await postDownloadEntitesData(
      organisation,
      ENTITIES.invoices,
      {
        filters: {
          and: [
            ['invoices.id', SEARCH_OPERATORS_PRIMITIVE.in, [invoiceId]],
          ],
        },
        options: invoiceOptions,
      },
      invoiceOptions,
      DownloadOption.download,
    );
  };

  const handleSendTaxReceipt = async (paymentId: number) => {
    const options = {
      file: `tax_receipt_${paymentId}.pdf`,
      orientation: ORIENTATION.portrait,
      format: FORMAT.pdf,
      delivery: DELIVERY_TYPE.contact,
      payment_receipt: false,
    };
    const response = await postEmailEntitiesData(organisation, ENTITIES.taxReceipts, {
      filters: {
        and: [
          ['tax_receipts.payment_id', SEARCH_OPERATORS_PRIMITIVE.in, [paymentId]],
        ],
      },
      options,
    }, options);
    if (response.success) {
      dispatch(setAlert({ type: ALERT_TYPE.success, code: 'messages.tax_receipt_sent' }));
    } else {
      dispatch(setAlert({ type: ALERT_TYPE.error, code: 'messages.tax_receipt_sent_error' }));
    }
  };

  const handleDownloadTaxReceipt = async (paymentId: number) => {
    const options = {
      file: `tax_receipt_${paymentId}.pdf`,
      orientation: ORIENTATION.portrait,
      format: FORMAT.pdf,
      delivery: DELIVERY_TYPE.download,
      payment_receipt: false,
    };
    await postDownloadEntitesData(
      organisation,
      ENTITIES.taxReceipts,
      {
        filters: {
          and: [
            ['tax_receipts.payment_id', SEARCH_OPERATORS_PRIMITIVE.in, [paymentId]],
          ],
        },
        options,
      },
      options,
      DownloadOption.download,
    );
  };

  const handleEditFundraising = (eventId: number) => {
    dispatch(setSelections({
      entity: ENTITIES.fundraisings,
      selections: [eventId],
    }));
    dispatch(openModal({ modal: MODALS.editFundraising }));
  };

  const handleShowTransactions = (eventId: number) => {
    window.open(`/${Path.FUNDRAISING_TRANSACTION.replace(':fundraising_id', String(eventId))}`, '_blank');
  };

  const handleEditContact = (contactId: number) => {
    dispatch(setSelections({
      entity: ENTITIES.contacts,
      selections: [contactId],
    }));
    dispatch(openModal({ modal: MODALS.editContacts }));
  };

  const handleRetryPayment = async (item: DeclinedPaymentItem) => {
    const response = await getPaymentData(item.paymentId, organisation);
    if (response.success) {
      dispatch(openDialog({
        dialog: DIALOGS.retryPayments,
        payload: [response.data],
      }));
    }
  };

  const [collectedToday, setCollectedToday] = useState<DashboardCardData>(
    { total: 0, count: 0 },
  );
  const [collectedLoading, setCollectedLoading] = useState(true);

  const [declinedPayments, setDeclinedPayments] = useState<DashboardCardData>(
    { total: 0, count: 0 },
  );
  const [declinedLoading, setDeclinedLoading] = useState(true);

  const [unpaidDonations, setUnpaidDonations] = useState<DashboardCardData>(
    { total: 0, count: 0 },
  );
  const [unpaidLoading, setUnpaidLoading] = useState(true);

  const [scheduledPayments, setScheduledPayments] = useState<DashboardCardData>(
    { total: 0, count: 0 },
  );
  const [scheduledLoading, setScheduledLoading] = useState(true);

  const [fundraisingSummary, setFundraisingSummary] = useState<DashboardCardData>(
    { total: 0, count: 0 },
  );
  const [fundraisingLoading, setFundraisingLoading] = useState(true);

  const [declinedList, setDeclinedList] = useState<DeclinedPaymentItem[]>([]);
  const [declinedListLoading, setDeclinedListLoading] = useState(true);
  const [tableExpanded, setTableExpanded] = useState(false);

  const [unpaidList, setUnpaidList] = useState<UnpaidDonationItem[]>([]);
  const [unpaidListLoading, setUnpaidListLoading] = useState(true);
  const [unpaidExpanded, setUnpaidExpanded] = useState(false);

  const [scheduledList, setScheduledList] = useState<ScheduledPaymentItem[]>([]);
  const [scheduledListLoading, setScheduledListLoading] = useState(true);
  const [scheduledExpanded, setScheduledExpanded] = useState(false);

  const [eventsList, setEventsList] = useState<EventItem[]>([]);
  const [eventsListLoading, setEventsListLoading] = useState(true);
  const [eventsExpanded, setEventsExpanded] = useState(false);
  const [donorsExpanded, setDonorsExpanded] = useState(false);

  const [recentList, setRecentList] = useState<RecentPaymentItem[]>([]);
  const [recentListLoading, setRecentListLoading] = useState(true);
  const [recentExpanded, setRecentExpanded] = useState(false);
  const [recentPeriod, setRecentPeriod] = useState('today');

  const [topDonors, setTopDonors] = useState<TopDonorItem[]>([]);
  const [topDonorsLoading, setTopDonorsLoading] = useState(true);

  const [refreshingDeclined, setRefreshingDeclined] = useState(false);
  const [refreshingUnpaid, setRefreshingUnpaid] = useState(false);
  const [refreshingRecent, setRefreshingRecent] = useState(false);
  const [refreshingScheduled, setRefreshingScheduled] = useState(false);
  const [refreshingEvents, setRefreshingEvents] = useState(false);
  const [refreshingDonors, setRefreshingDonors] = useState(false);

  const [declinedPeriod, setDeclinedPeriod] = useState('all');
  const [unpaidPeriod, setUnpaidPeriod] = useState('all');
  const [scheduledPeriod, setScheduledPeriod] = useState('all');

  const [declinedFilter, setDeclinedFilter] = useState('');
  const [unpaidFilter, setUnpaidFilter] = useState('');
  const [recentFilter, setRecentFilter] = useState('');
  const [scheduledFilter, setScheduledFilter] = useState('');

  const declinedRef = useRef<HTMLDivElement>(null);
  const unpaidRef = useRef<HTMLDivElement>(null);
  const recentRef = useRef<HTMLDivElement>(null);
  const scheduledRef = useRef<HTMLDivElement>(null);
  const eventsRef = useRef<HTMLDivElement>(null);

  const scrollTo = (ref: React.RefObject<HTMLDivElement>, setExpanded: (v: boolean) => void) => {
    setExpanded(true);
    requestAnimationFrame(() => {
      const top = ref.current?.getBoundingClientRect().top ?? 0;
      const offset = top + window.pageYOffset - 80;
      window.scrollTo({ top: offset, behavior: 'smooth' });
    });
  };

  const isToday = (rawDate: string): boolean => {
    if (!rawDate) return false;
    return new Date(rawDate).toDateString() === new Date().toDateString();
  };

  const isWithinLastDays = (rawDate: string, days: number): boolean => {
    if (!rawDate) return false;
    const d = new Date(rawDate);
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - days);
    cutoff.setHours(0, 0, 0, 0);
    return d >= cutoff;
  };

  const isWithinNextDays = (rawDate: string, days: number): boolean => {
    if (!rawDate) return false;
    const d = new Date(rawDate);
    const now = new Date();
    const cutoff = new Date(now);
    cutoff.setDate(now.getDate() + days);
    return d >= now && d <= cutoff;
  };

  const matchesFilter = (searchText: string, donor: string, title?: string): boolean => {
    if (!searchText.trim()) return true;
    const lower = searchText.toLowerCase();
    if (donor.toLowerCase().includes(lower)) return true;
    if (title && title.toLowerCase().includes(lower)) return true;
    return false;
  };

  const sortedEventsList = useMemo(
    () => [...eventsList].sort((a, b) => Number(b.id) - Number(a.id)),
    [eventsList],
  );

  const filteredDeclinedList = useMemo(() => {
    const list = (() => {
      switch (declinedPeriod) {
        case 'today': return declinedList.filter((i) => isToday(i.rawDate));
        case 'lastWeek': return declinedList.filter((i) => isWithinLastDays(i.rawDate, 7));
        case 'lastMonth': return declinedList.filter((i) => isWithinLastDays(i.rawDate, 30));
        default: return declinedList;
      }
    })();
    return [...list]
      .filter((i) => matchesFilter(declinedFilter, i.donor))
      .sort(
        (a, b) => new Date(b.rawDate).getTime() - new Date(a.rawDate).getTime(),
      );
  }, [declinedList, declinedPeriod, declinedFilter]);

  const filteredUnpaidList = useMemo(() => {
    const list = (() => {
      switch (unpaidPeriod) {
        case 'today': return unpaidList.filter((i) => i.daysOverdue === 0);
        case 'lastWeek': return unpaidList.filter((i) => i.daysOverdue >= 0 && i.daysOverdue <= 7);
        case 'lastMonth': return unpaidList.filter((i) => i.daysOverdue >= 0 && i.daysOverdue <= 30);
        default: return unpaidList;
      }
    })();
    return [...list]
      .filter((i) => matchesFilter(unpaidFilter, i.donor, i.title))
      .sort(
        (a, b) => new Date(b.rawDate).getTime() - new Date(a.rawDate).getTime(),
      );
  }, [unpaidList, unpaidPeriod, unpaidFilter]);

  const filteredScheduledList = useMemo(() => {
    const list = (() => {
      switch (scheduledPeriod) {
        case 'today': return scheduledList.filter((i) => isToday(i.rawDate));
        case 'tomorrow': return scheduledList.filter((i) => {
          if (!i.rawDate) return false;
          const tomorrow = new Date();
          tomorrow.setDate(tomorrow.getDate() + 1);
          return new Date(i.rawDate).toDateString() === tomorrow.toDateString();
        });
        case 'thisWeek': return scheduledList.filter((i) => isWithinNextDays(i.rawDate, 7));
        case 'month': {
          const now = new Date();
          const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
          const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
          return scheduledList.filter((i) => {
            if (!i.rawDate) return false;
            const d = new Date(i.rawDate);
            return d >= monthStart && d <= monthEnd;
          });
        }
        default: return scheduledList;
      }
    })();
    return [...list]
      .filter((i) => matchesFilter(scheduledFilter, i.donor))
      .sort(
        (a, b) => new Date(b.rawDate).getTime() - new Date(a.rawDate).getTime(),
      );
  }, [scheduledList, scheduledPeriod, scheduledFilter]);

  const filteredRecentList = useMemo(() => {
    const list = (() => {
      switch (recentPeriod) {
        case 'today': return recentList.filter((i) => isToday(i.rawDate));
        case 'lastWeek': return recentList.filter((i) => isWithinLastDays(i.rawDate, 7));
        case 'lastMonth': return recentList.filter((i) => isWithinLastDays(i.rawDate, 30));
        default: return recentList;
      }
    })();
    return [...list]
      .filter((i) => matchesFilter(recentFilter, i.donor))
      .sort(
        (a, b) => new Date(b.rawDate).getTime() - new Date(a.rawDate).getTime(),
      );
  }, [recentList, recentPeriod, recentFilter]);

  const handleDownloadDeclined = () => {
    const dateFilters = buildDateFilter('payments.scheduled_date', declinedPeriod as DownloadPeriod);
    const filters = {
      and: [
        ['payments.payment_status', SEARCH_OPERATORS_PRIMITIVE.in,
          [PAYMENT_STATUS.declined, PAYMENT_STATUS.error],
        ],
        ...dateFilters,
      ],
    };
    dispatch(openDialog({
      dialog: DIALOGS.dashboardDownload,
      payload: { entity: ENTITIES.payments, filters, defaultFileName: 'declined-payments' },
    }));
  };

  const handleDownloadRecent = () => {
    const dateFilters = buildDateFilter('payments.scheduled_date', recentPeriod as DownloadPeriod);
    if (dateFilters.length === 0) {
      const now = new Date();
      const start = new Date(now);
      start.setDate(now.getDate() - 30);
      start.setHours(0, 0, 0, 0);
      dateFilters.push(['payments.scheduled_date', SEARCH_OPERATORS_PRIMITIVE.gte, start.toISOString()]);
    }
    const filters = {
      and: [
        ...dateFilters,
        ['payments.payment_status', SEARCH_OPERATORS_PRIMITIVE.eq, PAYMENT_STATUS.paid],
      ],
    };
    dispatch(openDialog({
      dialog: DIALOGS.dashboardDownload,
      payload: { entity: ENTITIES.payments, filters, defaultFileName: 'recent-payments' },
    }));
  };

  const handleDownloadUnpaid = () => {
    const dateFilters = buildDateFilter('invoices.due_date', unpaidPeriod as DownloadPeriod);
    const filters = {
      and: [
        ['invoices.payment_status', SEARCH_OPERATORS_PRIMITIVE.in,
          [INVOICE_PAYMENT_STATUS.not_paid, INVOICE_PAYMENT_STATUS.partially_paid],
        ],
        ['invoices.state', SEARCH_OPERATORS_PRIMITIVE.eq, INVOICE_STATE.final],
        ['invoices.balance', SEARCH_OPERATORS_PRIMITIVE.gt, '0'],
        ...dateFilters,
      ],
    };
    dispatch(openDialog({
      dialog: DIALOGS.dashboardDownload,
      payload: { entity: ENTITIES.invoices, filters, defaultFileName: 'unpaid-donations' },
    }));
  };

  const handleDownloadScheduled = () => {
    const dateFilters = buildDateFilter('payments.scheduled_date', scheduledPeriod as DownloadPeriod);
    if (dateFilters.length === 0) {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      const end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
      dateFilters.push(
        ['payments.scheduled_date', SEARCH_OPERATORS_PRIMITIVE.gte, start.toISOString()],
        ['payments.scheduled_date', SEARCH_OPERATORS_PRIMITIVE.lte, end.toISOString()],
      );
    }
    const filters = {
      and: [
        ...dateFilters,
        ['payments.payment_status', SEARCH_OPERATORS_PRIMITIVE.eq, PAYMENT_STATUS.scheduled],
      ],
    };
    dispatch(openDialog({
      dialog: DIALOGS.dashboardDownload,
      payload: { entity: ENTITIES.payments, filters, defaultFileName: 'scheduled-payments' },
    }));
  };

  const handleRefreshDeclined = async () => {
    setRefreshingDeclined(true);
    try {
      const { summary, items } = await fetchDeclinedPaymentsData(organisation);
      setDeclinedPayments(summary);
      setDeclinedList(items);
    } finally {
      setRefreshingDeclined(false);
    }
  };

  const handleRefreshUnpaid = async () => {
    setRefreshingUnpaid(true);
    try {
      const { summary, items } = await fetchUnpaidDonationsData(organisation);
      setUnpaidDonations(summary);
      setUnpaidList(items);
    } finally {
      setRefreshingUnpaid(false);
    }
  };

  const handleRefreshRecent = async () => {
    setRefreshingRecent(true);
    try {
      const [summary, list] = await Promise.all([
        fetchCollectedToday(organisation),
        fetchRecentPaymentsList(organisation),
      ]);
      setCollectedToday(summary);
      setRecentList(list);
    } finally {
      setRefreshingRecent(false);
    }
  };

  const handleRefreshScheduled = async () => {
    setRefreshingScheduled(true);
    try {
      const [summary, list] = await Promise.all([
        fetchScheduledPayments(organisation),
        fetchScheduledPaymentsList(organisation),
      ]);
      setScheduledPayments(summary);
      setScheduledList(list);
    } finally {
      setRefreshingScheduled(false);
    }
  };

  const handleRefreshEvents = async () => {
    setRefreshingEvents(true);
    try {
      const { summary, items } = await fetchEventsData(organisation);
      setFundraisingSummary(summary);
      setEventsList(items);
    } finally {
      setRefreshingEvents(false);
    }
  };

  const handleRefreshDonors = async () => {
    setRefreshingDonors(true);
    try {
      const donors = await fetchTopDonors(organisation);
      setTopDonors(donors);
    } finally {
      setRefreshingDonors(false);
    }
  };

  const monthLabel = useMemo(() => {
    const locale = lang === 'fr' ? 'fr-CA' : 'en-US';
    return new Date().toLocaleString(locale, { month: 'long' });
  }, [lang]);

  const currencyCode = useMemo(() => {
    const currency = currencies.find((item) => item.id === organisationCurrencyId);
    return currency?.name ?? 'CAD';
  }, [currencies, organisationCurrencyId]);

  const formatCurrency = (amount: number): string => {
    const locale = lang === 'fr' ? 'fr-CA' : 'en-US';
    return amount.toLocaleString(locale, { style: 'currency', currency: currencyCode });
  };

  useEffect(() => {
    dispatch(getCurrenciesAction());
  }, []);

  useEffect(() => {
    Promise.all([
      fetchCollectedToday(organisation),
      fetchScheduledPayments(organisation),
    ]).then(([collectedResult, scheduledResult]) => {
      setCollectedToday(collectedResult);
      setScheduledPayments(scheduledResult);
      setCollectedLoading(false);
      setScheduledLoading(false);
    });

    dispatch(getPresetsAction({ entity: ENTITIES.payments }));
    dispatch(getPresetsAction({ entity: ENTITIES.invoices }));

    fetchDeclinedPaymentsData(organisation)
      .then(({ summary, items }) => {
        setDeclinedPayments(summary);
        setDeclinedList(items);
      })
      .finally(() => {
        setDeclinedLoading(false);
        setDeclinedListLoading(false);
      });

    fetchUnpaidDonationsData(organisation)
      .then(({ summary, items }) => {
        setUnpaidDonations(summary);
        setUnpaidList(items);
      })
      .finally(() => {
        setUnpaidLoading(false);
        setUnpaidListLoading(false);
      });

    fetchScheduledPaymentsList(organisation)
      .then(setScheduledList)
      .finally(() => setScheduledListLoading(false));

    fetchEventsData(organisation)
      .then(({ summary, items }) => {
        setFundraisingSummary(summary);
        setEventsList(items);
      })
      .finally(() => {
        setFundraisingLoading(false);
        setEventsListLoading(false);
      });

    fetchRecentPaymentsList(organisation)
      .then(setRecentList)
      .finally(() => setRecentListLoading(false));

    fetchTopDonors(organisation)
      .then(setTopDonors)
      .finally(() => setTopDonorsLoading(false));
  }, [organisation]);

  const dashboardLabel = (key: string) => t(lang, `pages.dashboard.${key}`);

  return (
    <div className={classes.wrapper}>
      <Box aria-label="Overview" className={classes.grid}>
        <SummaryCard
          icon={<ReceiptIcon color={COLORS.action} />}
          label={dashboardLabel('collectedToday')}
          value={formatCurrency(collectedToday.total)}
          valueColor={COLORS.action}
          subtitle={`${collectedToday.count} ${dashboardLabel('payments')} · +${collectedToday.percentChange ?? 0}% ${dashboardLabel('versusYesterday')}`}
          loading={collectedLoading}
          hoverColor={COLORS.actionLight}
          onClick={() => scrollTo(recentRef, setRecentExpanded)} />
        <SummaryCard
          icon={<XCircleIcon />}
          label={dashboardLabel('declinedPayments')}
          value={formatCurrency(declinedPayments.total)}
          valueColor={COLORS.red}
          subtitle={`${declinedPayments.count} ${dashboardLabel('paymentsToReview')}`}
          loading={declinedLoading}
          hoverColor={COLORS.redLight}
          onClick={() => scrollTo(declinedRef, setTableExpanded)} />
        <SummaryCard
          icon={<AlertTriangleIcon />}
          label={dashboardLabel('unpaidDonations')}
          value={formatCurrency(unpaidDonations.total)}
          valueColor={COLORS.amber}
          subtitle={`${unpaidDonations.count} ${dashboardLabel('pledges')} · ${dashboardLabel('oldestDays')} ${unpaidDonations.oldestDays ?? 0} ${dashboardLabel('days')}`}
          loading={unpaidLoading}
          hoverColor={COLORS.amberLight}
          onClick={() => scrollTo(unpaidRef, setUnpaidExpanded)} />
        <SummaryCard
          icon={<CalendarClockIcon />}
          label={`${dashboardLabel('scheduled')} (${monthLabel})`}
          value={formatCurrency(scheduledPayments.total)}
          valueColor={COLORS.primary}
          subtitle={`${scheduledPayments.count} ${dashboardLabel('paymentsToCome')}`}
          loading={scheduledLoading}
          hoverColor={COLORS.primaryLight}
          onClick={() => scrollTo(scheduledRef, setScheduledExpanded)} />
        <SummaryCard
          icon={<LinkIcon color={COLORS.secondary} />}
          label={dashboardLabel('fundraisingEvents')}
          value={formatCurrency(fundraisingSummary.total)}
          valueColor={COLORS.secondary}
          subtitle={`${fundraisingSummary.count} ${dashboardLabel('eventsCount')}`}
          loading={fundraisingLoading}
          hoverColor={COLORS.secondaryLight}
          onClick={() => scrollTo(eventsRef, setEventsExpanded)} />
      </Box>

      <div className={classes.rowGrid}>
        <div className={classes.section} ref={declinedRef}>
          {declinedListLoading && (
          <div className={classes.sectionOverlay}>
            <CircularProgress size={24} />
          </div>
          )}
          <div
            className={classes.sectionHeader}
            onClick={() => setTableExpanded(!tableExpanded)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter') setTableExpanded(!tableExpanded); }}>
            <div className={classes.sectionTitle} style={{ color: COLORS.red }}>
              <ExpandMoreIcon
                style={{ fontSize: 16 }}
                className={[classes.chevron, tableExpanded ? classes.chevronExpanded : ''].join(' ')} />
              <XCircleIcon color={COLORS.red} />
              {dashboardLabel('declinedPayments')}
              <span className={classes.totalBadge}>
                {formatCurrency(filteredDeclinedList.reduce((s, i) => s + i.amount, 0))}
              </span>
            </div>
            <div className={classes.actionRow}>
              <span
                className={classes.badge}
                style={{ backgroundColor: COLORS.redLight, color: COLORS.red }}>
                {filteredDeclinedList.length}
              </span>
              <div className={classes.actionButtons}>
                <Tooltip title={t(lang, 'misc.downloadTheList') as string} placement="top-start">
                  <button
                    type="button"
                    className={classes.downloadButton}
                    onClick={(e) => { e.stopPropagation(); handleDownloadDeclined(); }}>
                    <GetAppIcon style={{ fontSize: 14 }} />
                    {t(lang, 'misc.download')}
                  </button>
                </Tooltip>
                <Tooltip title={t(lang, 'misc.refreshTheList') as string} placement="top-start">
                  <button
                    type="button"
                    className={classes.primaryButton}
                    onClick={(e) => { e.stopPropagation(); handleRefreshDeclined(); }}
                    disabled={refreshingDeclined}>
                    {refreshingDeclined ? (
                      <CircularProgress size={18} style={{ color: COLORS.primary }} />
                    ) : (
                      <ReplayIcon style={{ fontSize: 14 }} />
                    )}
                    {t(lang, 'misc.refresh')}
                  </button>
                </Tooltip>
              </div>
            </div>
          </div>

          {tableExpanded && (
          <>
            <div className={classes.filterRow}>
              <TimePeriodTabs
                keys={['all', 'today', 'lastWeek', 'lastMonth']}
                active={declinedPeriod}
                onChange={setDeclinedPeriod}
                getLabel={(key) => dashboardLabel(`period${key.charAt(0).toUpperCase() + key.slice(1)}`)} />
              <TextField
                className={classes.filterInput}
                placeholder={dashboardLabel('filter')}
                value={declinedFilter}
                onChange={(e) => setDeclinedFilter(e.target.value)}
                variant="outlined"
                size="small"
                InputProps={{
                  startAdornment: <SearchIcon style={{ fontSize: 16, color: '#94A3B8' }} />,
                }} />
            </div>
            <div className={classes.listWrapper}>
              {declinedListLoading ? (
                <div style={{ textAlign: 'center', padding: 24 }}>...</div>
              ) : filteredDeclinedList.map((item) => (
                <Row
                  key={item.id}
                  className={classes.listItem}
                  onDoubleClick={() => handleEditPayment(item.paymentId)}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className={classes.donorName}>{item.donor}</div>
                    <div className={classes.donorDetail}>{item.paymentInfoDetail}</div>
                    <div className={classes.donorDetail}>
                      {item.date} · {item.id} · {item.reason}
                    </div>
                  </div>
                  <span className={classes.amount}>{formatCurrency(item.amount)}</span>
                  <Tooltip title={t(lang, 'misc.retryThePayment') as string} placement="top-start">
                    <button
                      type="button"
                      className={classes.ghostButton}
                      onClick={() => handleRetryPayment(item)}>
                      <ReplayIcon style={{ fontSize: 12 }} />
                    </button>
                  </Tooltip>
                </Row>
              ))}
            </div>
          </>
          )}
        </div>

        <div className={classes.section} ref={unpaidRef}>
          {unpaidListLoading && (
          <div className={classes.sectionOverlay}>
            <CircularProgress size={24} />
          </div>
          )}
          <div
            className={classes.sectionHeader}
            onClick={() => setUnpaidExpanded(!unpaidExpanded)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter') setUnpaidExpanded(!unpaidExpanded); }}>
            <div className={classes.sectionTitle} style={{ color: COLORS.amber }}>
              <ExpandMoreIcon
                style={{ fontSize: 16 }}
                className={[classes.chevron, unpaidExpanded ? classes.chevronExpanded : ''].join(' ')} />
              <AlertTriangleIcon color={COLORS.amber} />
              {dashboardLabel('unpaidDonations')}
              <span className={classes.warningBadge}>
                {formatCurrency(filteredUnpaidList.reduce((s, i) => s + i.amount, 0))}
              </span>
            </div>
            <div className={classes.actionRow}>
              <span
                className={classes.badge}
                style={{ backgroundColor: COLORS.amberLight, color: COLORS.amber }}>
                {filteredUnpaidList.length}
              </span>
              <div className={classes.actionButtons}>
                <Tooltip title={t(lang, 'misc.downloadTheList') as string} placement="top-start">
                  <button
                    type="button"
                    className={classes.downloadButton}
                    onClick={(e) => { e.stopPropagation(); handleDownloadUnpaid(); }}>
                    <GetAppIcon style={{ fontSize: 14 }} />
                    {t(lang, 'misc.download')}
                  </button>
                </Tooltip>
                <Tooltip title={t(lang, 'misc.refreshTheList') as string} placement="top-start">
                  <button
                    type="button"
                    className={classes.primaryButton}
                    onClick={(e) => { e.stopPropagation(); handleRefreshUnpaid(); }}
                    disabled={refreshingUnpaid}>
                    {refreshingUnpaid ? (
                      <CircularProgress size={18} style={{ color: COLORS.primary }} />
                    ) : (
                      <ReplayIcon style={{ fontSize: 14 }} />
                    )}
                    {t(lang, 'misc.refresh')}
                  </button>
                </Tooltip>
              </div>
            </div>
          </div>

          {unpaidExpanded && (
          <>
            <div className={classes.filterRow}>
              <TimePeriodTabs
                keys={['all', 'today', 'lastWeek', 'lastMonth']}
                active={unpaidPeriod}
                onChange={setUnpaidPeriod}
                getLabel={(key) => dashboardLabel(`period${key.charAt(0).toUpperCase() + key.slice(1)}`)} />
              <TextField
                className={classes.filterInput}
                placeholder={dashboardLabel('filter')}
                value={unpaidFilter}
                onChange={(e) => setUnpaidFilter(e.target.value)}
                variant="outlined"
                size="small"
                InputProps={{
                  startAdornment: <SearchIcon style={{ fontSize: 16, color: '#94A3B8' }} />,
                }} />
            </div>
            <div className={classes.listWrapper}>
              {filteredUnpaidList.map((item) => (
                <Row
                  key={item.id}
                  className={classes.listItem}
                  onDoubleClick={() => handleEditInvoice(item.paymentId)}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className={classes.donorName}>{item.donor}</div>
                    {item.title ? <div className={classes.donorDetail}>{item.title}</div> : null}
                    <div className={classes.donorDetail}>
                      {item.dueDate} · {item.id} · {item.daysOverdue} {dashboardLabel('days')}
                    </div>
                  </div>
                  <span className={classes.amount}>{formatCurrency(item.amount)}</span>
                  <Tooltip title={t(lang, 'misc.downloadTheInvoice') as string} placement="top-start">
                    <button
                      type="button"
                      className={classes.downloadButton}
                      onClick={() => handleDownloadInvoice(item.paymentId)}>
                      <GetAppIcon style={{ fontSize: 12 }} />
                    </button>
                  </Tooltip>
                  <Tooltip title={t(lang, 'misc.sendTheInvoice') as string} placement="top-start">
                    <button
                      type="button"
                      className={classes.ghostButton}
                      onClick={() => handleSendInvoice(item.paymentId)}>
                      <SendIcon style={{ fontSize: 12 }} />
                    </button>
                  </Tooltip>
                </Row>
              ))}
            </div>
          </>
          )}
        </div>
      </div>

      <div className={classes.rowGrid}>
        <div className={classes.section} ref={recentRef}>
          {recentListLoading && (
            <div className={classes.sectionOverlay}>
              <CircularProgress size={24} />
            </div>
          )}
          <div
            className={classes.sectionHeader}
            onClick={() => setRecentExpanded(!recentExpanded)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter') setRecentExpanded(!recentExpanded); }}>
            <div className={classes.sectionTitle} style={{ color: COLORS.action }}>
              <ExpandMoreIcon
                style={{ fontSize: 16 }}
                className={[classes.chevron, recentExpanded ? classes.chevronExpanded : ''].join(' ')} />
              <ReceiptIcon color={COLORS.action} />
              {dashboardLabel('recentPayments')}
              <span
                className={classes.badge}
                style={{ backgroundColor: COLORS.actionLight, color: COLORS.action }}>
                {formatCurrency(filteredRecentList.reduce((s, i) => s + i.amount, 0))}
              </span>
            </div>
            <div className={classes.actionRow}>
              <span
                className={classes.badge}
                style={{ backgroundColor: COLORS.actionLight, color: COLORS.action }}>
                {filteredRecentList.length}
              </span>
              <div className={classes.actionButtons}>
                <Tooltip title={t(lang, 'misc.downloadTheList') as string} placement="top-start">
                  <button
                    type="button"
                    className={classes.downloadButton}
                    onClick={(e) => { e.stopPropagation(); handleDownloadRecent(); }}>
                    <GetAppIcon style={{ fontSize: 14 }} />
                    {t(lang, 'misc.download')}
                  </button>
                </Tooltip>
                <Tooltip title={t(lang, 'misc.refreshTheList') as string} placement="top-start">
                  <button
                    type="button"
                    className={classes.primaryButton}
                    onClick={(e) => { e.stopPropagation(); handleRefreshRecent(); }}
                    disabled={refreshingRecent}>
                    {refreshingRecent ? (
                      <CircularProgress size={18} style={{ color: COLORS.primary }} />
                    ) : (
                      <ReplayIcon style={{ fontSize: 14 }} />
                    )}
                    {t(lang, 'misc.refresh')}
                  </button>
                </Tooltip>
              </div>
            </div>
          </div>

          {recentExpanded && (
          <>
            <div className={classes.filterRow}>
              <TimePeriodTabs
                keys={['today', 'lastWeek', 'lastMonth']}
                active={recentPeriod}
                onChange={setRecentPeriod}
                getLabel={(key) => dashboardLabel(`period${key.charAt(0).toUpperCase() + key.slice(1)}`)} />
              <TextField
                className={classes.filterInput}
                placeholder={dashboardLabel('filter')}
                value={recentFilter}
                onChange={(e) => setRecentFilter(e.target.value)}
                variant="outlined"
                size="small"
                InputProps={{
                  startAdornment: <SearchIcon style={{ fontSize: 16, color: '#94A3B8' }} />,
                }} />
            </div>
            <div className={classes.listWrapper}>
              {filteredRecentList.map((item) => (
                <Row
                  key={item.id}
                  className={classes.listItem}
                  onDoubleClick={() => handleEditPayment(item.paymentId)}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className={classes.donorName}>{item.donor}</div>
                    <div className={classes.donorDetail}>{item.paymentInfoDetail}</div>
                    <div className={classes.donorDetail}>
                      {item.date}
                    </div>
                  </div>
                  <span className={classes.amount}>{formatCurrency(item.amount)}</span>
                  <Tooltip title={t(lang, 'misc.downloadTheTaxReceipt') as string} placement="top-start">
                    <button
                      type="button"
                      className={classes.downloadButton}
                      onClick={() => handleDownloadTaxReceipt(item.paymentId)}>
                      <GetAppIcon style={{ fontSize: 12 }} />
                    </button>
                  </Tooltip>
                  <Tooltip title={t(lang, 'misc.sendTheTaxReceipt') as string} placement="top-start">
                    <button
                      type="button"
                      className={classes.ghostButton}
                      onClick={() => handleSendTaxReceipt(item.paymentId)}>
                      <SendIcon style={{ fontSize: 12 }} />
                    </button>
                  </Tooltip>
                </Row>
              ))}
            </div>
          </>
          )}
        </div>
        <div className={classes.section} ref={scheduledRef}>
          {scheduledListLoading && (
          <div className={classes.sectionOverlay}>
            <CircularProgress size={24} />
          </div>
          )}
          <div
            className={classes.sectionHeader}
            onClick={() => setScheduledExpanded(!scheduledExpanded)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter') setScheduledExpanded(!scheduledExpanded); }}>
            <div className={classes.sectionTitle} style={{ color: COLORS.primary }}>
              <ExpandMoreIcon
                style={{ fontSize: 16 }}
                className={[classes.chevron, scheduledExpanded ? classes.chevronExpanded : ''].join(' ')} />
              <CalendarClockIcon color={COLORS.primary} />
              {dashboardLabel('scheduledPayments')}
              <span className={classes.scheduledBadge}>
                {formatCurrency(filteredScheduledList.reduce((s, i) => s + i.amount, 0))}
              </span>
            </div>
            <div className={classes.actionRow}>
              <span
                className={classes.badge}
                style={{ backgroundColor: COLORS.primaryLight, color: COLORS.primary }}>
                {filteredScheduledList.length}
              </span>
              <div className={classes.actionButtons}>
                <Tooltip title={t(lang, 'misc.downloadTheList') as string} placement="top-start">
                  <button
                    type="button"
                    className={classes.downloadButton}
                    onClick={(e) => { e.stopPropagation(); handleDownloadScheduled(); }}>
                    <GetAppIcon style={{ fontSize: 14 }} />
                    {t(lang, 'misc.download')}
                  </button>
                </Tooltip>
                <Tooltip title={t(lang, 'misc.refreshTheList') as string} placement="top-start">
                  <button
                    type="button"
                    className={classes.primaryButton}
                    onClick={(e) => { e.stopPropagation(); handleRefreshScheduled(); }}
                    disabled={refreshingScheduled}>
                    {refreshingScheduled ? (
                      <CircularProgress size={18} style={{ color: COLORS.primary }} />
                    ) : (
                      <ReplayIcon style={{ fontSize: 14 }} />
                    )}
                    {t(lang, 'misc.refresh')}
                  </button>
                </Tooltip>
              </div>
            </div>
          </div>

          {scheduledExpanded && (
          <>
            <div className={classes.filterRow}>
              <TimePeriodTabs
                keys={['all', 'today', 'tomorrow', 'thisWeek', 'month']}
                active={scheduledPeriod}
                onChange={setScheduledPeriod}
                getLabel={(key) => dashboardLabel(`period${key.charAt(0).toUpperCase() + key.slice(1)}`)} />
              <TextField
                className={classes.filterInput}
                placeholder={dashboardLabel('filter')}
                value={scheduledFilter}
                onChange={(e) => setScheduledFilter(e.target.value)}
                variant="outlined"
                size="small"
                InputProps={{
                  startAdornment: <SearchIcon style={{ fontSize: 16, color: '#94A3B8' }} />,
                }} />
            </div>
            <div className={classes.listWrapper}>
              {filteredScheduledList.map((item) => (
                <Row
                  key={item.id}
                  className={classes.listItem}
                  onDoubleClick={() => handleEditPayment(item.paymentId)}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className={classes.donorName}>{item.donor}</div>
                    <div className={classes.donorDetail}>{item.paymentInfoDetail}</div>
                    <div className={classes.donorDetail}>
                      {item.scheduledDate} · {item.id}
                    </div>
                  </div>
                  <span className={classes.amount}>{formatCurrency(item.amount)}</span>
                </Row>
              ))}
            </div>
          </>
          )}
        </div>
      </div>

      <div className={classes.bottomRow}>
        <div className={classes.section} ref={eventsRef}>
          {eventsListLoading && (
            <div className={classes.sectionOverlay}>
              <CircularProgress size={24} />
            </div>
          )}
          <div
            className={classes.sectionHeader}
            onClick={() => setEventsExpanded(!eventsExpanded)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter') setEventsExpanded(!eventsExpanded); }}>
            <div className={classes.sectionTitle} style={{ color: COLORS.secondary }}>
              <ExpandMoreIcon
                style={{ fontSize: 16 }}
                className={[classes.chevron, eventsExpanded ? classes.chevronExpanded : ''].join(' ')} />
              <LinkIcon color={COLORS.secondary} />
              {dashboardLabel('fundraisingEvents')}
              <span
                className={classes.badge}
                style={{ backgroundColor: COLORS.secondaryLight, color: COLORS.secondary }}>
                {formatCurrency(sortedEventsList.reduce((s, i) => s + i.raised, 0))}
              </span>
            </div>
            <div className={classes.actionRow}>
              <span
                className={classes.badge}
                style={{ backgroundColor: COLORS.secondaryLight, color: COLORS.secondary }}>
                {sortedEventsList.length}
              </span>
              <div className={classes.actionButtons}>
                <Tooltip title={t(lang, 'misc.refreshTheList') as string} placement="top-start">
                  <button
                    type="button"
                    className={classes.primaryButton}
                    onClick={(e) => { e.stopPropagation(); handleRefreshEvents(); }}
                    disabled={refreshingEvents}>
                    {refreshingEvents ? (
                      <CircularProgress size={18} style={{ color: COLORS.primary }} />
                    ) : (
                      <ReplayIcon style={{ fontSize: 14 }} />
                    )}
                    {t(lang, 'misc.refresh')}
                  </button>
                </Tooltip>
              </div>
            </div>
          </div>

          {eventsExpanded && (
            <>
              <div className={classes.listWrapper}>
                {sortedEventsList.map((item) => {
                  const pct = item.goal > 0
                    ? Math.min(100, Math.round((item.raised / item.goal) * 100))
                    : 0;
                  return (
                    <Row
                      key={item.id}
                      className={classes.listItem}
                      onDoubleClick={() => handleEditFundraising(item.eventId)}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className={classes.donorName}>{item.name}</div>
                        <div className={classes.donorDetail}>
                          {dashboardLabel('endDate')}: {item.endDate} · {formatCurrency(item.raised)} {dashboardLabel('of')} {formatCurrency(item.goal)}
                        </div>
                        <div className={classes.progressBar}>
                          <div className={classes.progressFill} style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                      <span className={classes.amount}>{formatCurrency(item.raised)}</span>
                      <Tooltip title={t(lang, 'misc.showFundraisingTransactions') as string} placement="top-start">
                        <button
                          type="button"
                          className={classes.primaryButton}
                          onClick={() => handleShowTransactions(item.eventId)}>
                          <DescriptionOutlinedIcon style={{ fontSize: 12 }} />
                        </button>
                      </Tooltip>
                    </Row>
                  );
                })}
              </div>
            </>
          )}
        </div>
        <div className={classes.section}>
          {topDonorsLoading && (
            <div className={classes.sectionOverlay}>
              <CircularProgress size={24} />
            </div>
          )}
          <div
            className={classes.sectionHeader}
            onClick={() => setDonorsExpanded(!donorsExpanded)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter') setDonorsExpanded(!donorsExpanded); }}>
            <div className={classes.sectionTitle} style={{ color: COLORS.secondary }}>
              <ExpandMoreIcon
                style={{ fontSize: 16 }}
                className={[classes.chevron, donorsExpanded ? classes.chevronExpanded : ''].join(' ')} />
              {dashboardLabel('topDonors')}
            </div>
            <div className={classes.actionRow}>
              <div className={classes.actionButtons}>
                <Tooltip title={t(lang, 'misc.refreshTheList') as string} placement="top-start">
                  <button
                    type="button"
                    className={classes.primaryButton}
                    onClick={(e) => { e.stopPropagation(); handleRefreshDonors(); }}
                    disabled={refreshingDonors}>
                    {refreshingDonors ? (
                      <CircularProgress size={18} style={{ color: COLORS.primary }} />
                    ) : (
                      <ReplayIcon style={{ fontSize: 14 }} />
                    )}
                    {t(lang, 'misc.refresh')}
                  </button>
                </Tooltip>
              </div>
            </div>
          </div>

          {donorsExpanded && (
            <>
              <div className={classes.listWrapper}>
                {topDonors.map((item, i) => (
                  <Row
                    key={item.donor}
                    className={classes.listItem}
                    onDoubleClick={() => handleEditContact(item.contactId)}>
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        padding: '2px 8px',
                        borderRadius: 999,
                        backgroundColor: COLORS.secondaryLight,
                        color: COLORS.secondary,
                      }}>
                      {i + 1}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className={classes.donorName}>{item.donor}</div>
                      <div className={classes.donorDetail}>
                        {item.gifts} {dashboardLabel('gifts')}
                      </div>
                    </div>
                    <span className={classes.amount}>{formatCurrency(item.total)}</span>
                  </Row>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
